import { observer } from "mobx-react-lite";
import * as React from "react";
import { Table } from "../../../newcomponents/table/table";
import { useTableKindId } from "../../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../../newcomponents/table/data/useTableItemQueryData";
import { NameDescriptionNoLinkColumn } from "../../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { TagsColumn } from "../../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../../newcomponents/table/columns/wellKnown/createdColumn";
import { TableHeaderRefreshButton } from "../../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../../newcomponents/table/components/TableCplnQuery";
import { linksOf } from "../../../services/cpln";
import { NGButton } from "../../../newcomponents/button/Button";
import { PlusCircle } from "react-feather";
import { TableModal } from "../../../components/antd/TableModal";
import { sortBy } from "lodash";
import { Secret } from "../../../schema/types/secret";
import { SecretTypeColumn } from "../../../newcomponents/table/columns/secret/typeColumn";
import { LastModifiedColumn } from "../../../newcomponents/table/columns/wellKnown/lastModifiedColumn";

interface SecretTableItem extends Secret {
  selfLink: string;
}

interface Props {
  setPullSecretLinks: React.Dispatch<React.SetStateAction<string[]>>;
}
const GVCCreatePullSecretsRaw: React.FC<Props> = ({ setPullSecretLinks }) => {
  const { kind, id } = useTableKindId("secret", "gvc-create-pullsecret-add");
  const qData = useTableItemQueryData<Secret>(kind, {
    fetchAllPages: true,
    query: {
      kind: "secret",
      spec: {
        match: "any",
        terms: [
          { property: "type", op: "=", value: "docker" },
          { property: "type", op: "=", value: "ecr" },
          { property: "type", op: "=", value: "gcp" },
        ],
      },
    },
  });

  const [currentItems, setCurrentItems] = React.useState<SecretTableItem[]>([]);
  function getCurrentLinks(): string[] {
    return currentItems.map((i) => i.selfLink);
  }
  React.useEffect(() => {
    setPullSecretLinks(getCurrentLinks());
  }, [currentItems, currentItems.length]);

  const [selectionsToRemove, setSelectionsToRemove] = React.useState<string[]>([]);
  const [selectionsToAdd, setSelectionsToAdd] = React.useState<string[]>([]);
  const [selectionItemsToAdd, setSelectionItemsToAdd] = React.useState<SecretTableItem[]>([]);
  const [isAddingItem, setIsAddingItem] = React.useState(false);

  function onRemove() {
    setCurrentItems((items) => items.filter((item) => !selectionsToRemove.includes(item.selfLink)));
    setSelectionsToRemove([]);
  }

  function onOkAddModal() {
    setCurrentItems((items) => {
      const newItems: SecretTableItem[] = [...items];
      const currentLinks = items.map((i) => i.selfLink);

      for (const itemToAdd of selectionItemsToAdd) {
        if (currentLinks.includes(itemToAdd.selfLink)) {
          continue;
        }

        newItems.push(itemToAdd);
      }

      return sortBy(newItems, "name");
    });

    setSelectionsToAdd([]);
    setSelectionItemsToAdd([]);
  }

  const secretTableItems: SecretTableItem[] = qData.visibleItems
    .map((item) => ({
      ...item,
      selfLink: linksOf(item).self!,
    }))
    .filter((item) => !getCurrentLinks().includes(item.selfLink));

  return (
    <>
      <Table<SecretTableItem>
        tableId={"gvc-create-pullsecrets"}
        title={"GVC Pull Secrets"}
        data={currentItems}
        selectMode="multi"
        selectKey="selfLink"
        selections={selectionsToRemove}
        onSelectionsChange={setSelectionsToRemove}
        columns={[
          NameDescriptionNoLinkColumn(), //
          SecretTypeColumn(),
          TagsColumn(),
          CreatedColumn(),
          LastModifiedColumn(),
        ]}
        headerRenderer={() => {
          return (
            <>
              <NGButton
                variant={"danger"}
                size={"small"}
                outlined
                disabled={selectionsToRemove.length < 1}
                onClick={onRemove}
              >
                Remove
              </NGButton>
              <NGButton variant={"action"} size={"small"} onClick={() => setIsAddingItem(true)}>
                Add
              </NGButton>
            </>
          );
        }}
        noItemsFoundRenderer={(table) => (
          <NGButton
            variant="primary"
            onClick={() => setIsAddingItem(true)}
            renderIcon={(_, props) => <PlusCircle {...props} />}
          >
            Add GVC Pull Secrets
          </NGButton>
        )}
      />
      <TableModal
        open={isAddingItem}
        onCancel={() => setIsAddingItem(false)}
        onOk={() => {
          onOkAddModal();
          setIsAddingItem(false);
        }}
      >
        <Table<SecretTableItem>
          tableId={id}
          title={"Select Pull Secrets to Add"}
          selectMode="multi"
          selectKey="selfLink"
          selections={selectionsToAdd}
          onSelectionsChange={(keys, items) => {
            setSelectionsToAdd(keys);
            setSelectionItemsToAdd(items!);
          }}
          data={secretTableItems}
          headerRenderer={() => {
            return (
              <>
                <TableHeaderRefreshButton onClick={qData.fetchItems} />
                <CustomTableCPLNQuery
                  tableId={id}
                  kind={kind}
                  onQuery={qData.setQuery}
                  isLoading={qData.isLoadingQueried}
                />
              </>
            );
          }}
          enableFilter
          isLoading={qData.isLoading}
          columns={[
            NameDescriptionNoLinkColumn(), //
            SecretTypeColumn(),
            TagsColumn(),
            CreatedColumn(),
            LastModifiedColumn(),
          ]}
        />
      </TableModal>
    </>
  );
};

export const GVCCreatePullSecrets = observer(GVCCreatePullSecretsRaw);
